/*
 *
 * The entities a patch is currently correcting.
 *
 * The patch's own annotations say *how many* there are; this is the list, so
 * that reviewing a wave is one click per entity rather than a catalog query per
 * entity. It loads on its own — a `"*"` patch can match thousands of entities,
 * and none of the rest of the page has to wait for that.
 *
 * Kinds are deliberately mixed in one table. A `"*"` patch conditions on a
 * value, not on a kind, so splitting by kind would be inventing a distinction
 * the patch does not make.
 *
 */

import { Entity } from '@backstage/catalog-model'
import { useApi } from '@backstage/core-plugin-api'
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
  TableColumn
} from '@backstage/core-components'
import {
  EntityRefLink,
  EntityTable,
  catalogApiRef,
  useEntity
} from '@backstage/plugin-catalog-react'
import useAsync from 'react-use/esm/useAsync'
import {
  globalPatchFilter,
  readPatchAppliedCount,
  readPatchDetails
} from './catalogPatchAnnotations'

const TITLE = 'Entities this patch corrects'

/**
 * A `"*"` patch on a defunct group name matched over 1300 entities during the
 * org-rename wave. Rendering all of them costs more than it tells anyone, and
 * the count in the card title is the number that matters.
 */
const FETCH_LIMIT = 500

/** Everything the three columns need, and nothing else. */
const FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'relations'
]

type PatchedEntities = {
  entities: Entity[]
  /** The full population, when more of it exists than was fetched. */
  truncatedFrom?: number
}

function isEntity(entity: Entity | undefined): entity is Entity {
  return entity !== undefined
}

function createKindColumn(): TableColumn<Entity> {
  return {
    title: 'Kind',
    field: 'kind',
    width: 'auto',
    cellStyle: { whiteSpace: 'nowrap' }
  }
}

function createNameColumn(): TableColumn<Entity> {
  return {
    title: 'Name',
    field: 'metadata.name',
    highlight: true,
    // No `defaultKind`: the table mixes kinds and carries a Kind column of its
    // own, so the name column is just the name.
    render: (entity: Entity) => (
      <EntityRefLink
        entityRef={entity}
        title={entity.metadata.title ?? entity.metadata.name}
      />
    )
  }
}

function usePatchedEntities() {
  const { entity } = useEntity()
  const catalogApi = useApi(catalogApiRef)

  const { targets } = readPatchDetails(entity)
  const filter = globalPatchFilter(entity)
  const reportedCount = readPatchAppliedCount(entity)

  return useAsync(async (): Promise<PatchedEntities> => {
    if (filter) {
      const { items } = await catalogApi.getEntities({
        filter,
        fields: FIELDS,
        limit: FETCH_LIMIT
      })
      const total = Math.max(reportedCount ?? 0, items.length)
      return {
        entities: items,
        truncatedFrom: total > items.length ? total : undefined
      }
    }

    if (targets.length === 0) {
      return { entities: [] }
    }

    const { items } = await catalogApi.getEntitiesByRefs({
      entityRefs: targets,
      fields: FIELDS
    })

    // A reference that does not resolve comes back as `undefined`. Those are
    // not rows — the patch reports them as `dangling` in its own status table.
    return { entities: items.filter(isEntity) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, JSON.stringify({ targets, filter, reportedCount })])
}

export function EntityCatalogPatchTargetsCard() {
  const { loading, error, value } = usePatchedEntities()

  if (loading) {
    return (
      <InfoCard title={TITLE}>
        <Progress />
      </InfoCard>
    )
  }

  if (error) {
    return (
      <InfoCard title={TITLE}>
        <ResponseErrorPanel error={error} />
      </InfoCard>
    )
  }

  const { entities, truncatedFrom } = value ?? { entities: [] }
  const counted = truncatedFrom
    ? `first ${entities.length} of ${truncatedFrom}`
    : `${entities.length}`

  return (
    <EntityTable
      title={`${TITLE} (${counted})`}
      entities={entities}
      emptyContent="No entity carries this patch right now."
      columns={[
        createKindColumn(),
        createNameColumn(),
        EntityTable.columns.createOwnerColumn()
      ]}
      tableOptions={{
        search: true,
        paging: true,
        pageSize: 20,
        pageSizeOptions: [20, 50, 100]
      }}
    />
  )
}
